/**
 * CookieConsentBanner · v2.6.9 batch CD
 * ======================================
 * Global, fixed-bottom cookie consent + data-compliance banner. Renders
 * once on every route until the visitor explicitly accepts or refuses.
 *
 * State machine:
 *  · localStorage key `rtg.cookie_consent.v1`
 *    · "accepted" → banner suppressed indefinitely for that browser
 *    · "refused"  → banner suppressed (but we already redirected them away)
 *    · null/missing → banner shows
 *  · Accept button writes "accepted" → banner unmounts → never re-renders
 *  · Opt-Out button:
 *      1. Clear localStorage + sessionStorage (kill any session token)
 *      2. window.location.href = "https://www.google.com" (hard-redirect)
 *  · "What are cookies?" info icon → inline drawer expands beneath the
 *    primary text without leaving the banner.
 *
 * Visual: full-width fixed bottom strip, 30 %-opacity zinc-900 background
 * (via rgba so the page behind shows through), white text + amber link for
 * AAA contrast. Slide-up entrance animation. Respects prefers-reduced-motion.
 */
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Info, X, ShieldCheck } from "lucide-react";

const STORAGE_KEY = "rtg.cookie_consent.v1";

// v2.6.9 batch CE · Banner is for marketing-site visitors only. Logged-in
// users inside the Admin Console, Rider Portal, Driver Dashboard, Meter,
// Trip flows, etc. already accepted Terms during their auth flow — we
// don't pester them with a marketing cookie banner on every internal page.
// If a route prefix appears in this list the banner is suppressed entirely.
const SUPPRESSED_PREFIXES = [
  "/admin",
  "/dashboard",
  "/ride",       // rider portal (ride/login, ride/book, ride/history)
  "/meter",      // driver live meter
  "/meter-statewide",
  "/login",      // driver login
  "/portal",     // legacy portal route
  "/dev-license",
  "/incident",
  "/group-ride",
  "/city",       // city flow
  "/custom",     // custom flow
  "/kiosk",      // v2.7.60+E43 · dispatch video wall / kiosk hardware — no consent UX needed
];

function readConsent() {
  try { return localStorage.getItem(STORAGE_KEY); }
  catch { return null; }
}
function writeConsent(value) {
  try { localStorage.setItem(STORAGE_KEY, value); }
  catch { /* private-mode storage blocked — silently no-op */ }
}

export default function CookieConsentBanner() {
  const [show, setShow] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const location = useLocation();

  // v2.6.9 batch CE · Suppress banner on every non-marketing route. Recheck
  // on every pathname change so an accepted user crossing into the rider
  // portal hides the banner if it was momentarily showing on /.
  const onSuppressedRoute = SUPPRESSED_PREFIXES.some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`));

  useEffect(() => {
    if (onSuppressedRoute) { setShow(false); return undefined; }
    // Defer 600 ms so we don't pop the banner on the first paint of a
    // logged-in user's portal load — feels less aggressive.
    const t = setTimeout(() => {
      if (readConsent() == null) setShow(true);
    }, 600);
    return () => clearTimeout(t);
  }, [onSuppressedRoute, location.pathname]);

  const accept = () => {
    writeConsent("accepted");
    setShow(false);
  };

  const optOut = () => {
    writeConsent("refused");
    try {
      localStorage.clear();
      sessionStorage.clear();
      // Belt + braces: re-write the refused flag AFTER clear so the user
      // doesn't get re-prompted if they happen to come back via the back
      // button before the redirect completes.
      writeConsent("refused");
    } catch { /* ignore storage clear failures */ }
    // Hard redirect away from the domain per spec.
    window.location.href = "https://www.google.com";
  };

  if (!show) return null;

  return (
    <div
      className="fixed bottom-0 inset-x-0 z-[60] w-full cookie-consent-enter"
      role="region"
      aria-label="Cookie consent"
      data-testid="cookie-consent-banner"
    >
      {/* v2.6.9 batch CF · 90 %-opacity dark backdrop per spec (was 30%).
          More opaque so the banner reads as a solid notification at the
          bottom of the screen rather than a faint hover overlay. */}
      <div className="border-t border-amber-400/60 backdrop-blur-md" style={{ backgroundColor: "rgba(10, 10, 10, 0.90)" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            {/* Shield + primary message + info toggle */}
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className="hidden sm:flex h-9 w-9 rounded-full bg-amber-400/90 text-zinc-950 items-center justify-center flex-shrink-0 shadow-md">
                <ShieldCheck className="h-5 w-5"/>
              </div>
              <div className="min-w-0">
                <p className="text-sm sm:text-base font-bold text-white leading-snug" data-testid="cookie-consent-message">
                  We do not sell our customer&apos;s information.{" "}
                  <Link
                    to="/legal/privacy"
                    className="text-amber-300 hover:text-amber-200 underline underline-offset-2 font-extrabold"
                    data-testid="cookie-consent-privacy-link"
                  >
                    Privacy Policy
                  </Link>
                  <button
                    type="button"
                    onClick={() => setExpanded((v) => !v)}
                    aria-label="What are cookies?"
                    aria-expanded={expanded}
                    className="ml-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-amber-300 transition-colors align-middle"
                    data-testid="cookie-consent-info-toggle"
                  >
                    <Info className="h-3 w-3"/>
                  </button>
                </p>
                <p className="text-[11px] text-zinc-300/90 mt-0.5">By continuing you agree to our use of operational cookies for dispatch + session validation.</p>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex items-center justify-end gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={optOut}
                className="px-4 py-2 text-[11px] uppercase tracking-widest font-bold text-zinc-200 hover:text-white border border-zinc-500/60 hover:border-white/80 rounded-md transition-colors"
                data-testid="cookie-consent-optout"
              >
                Opt-Out / Refuse
              </button>
              <button
                type="button"
                onClick={accept}
                className="px-5 py-2 text-[11px] uppercase tracking-widest font-extrabold text-zinc-950 bg-amber-400 hover:bg-amber-300 rounded-md transition-colors shadow-md shadow-amber-500/30"
                data-testid="cookie-consent-accept"
              >
                Accept
              </button>
            </div>
          </div>

          {/* Inline "What are cookies?" drawer */}
          {expanded && (
            <div
              className="mt-4 pt-4 border-t border-white/20 grid sm:grid-cols-[36px_1fr_36px] gap-3 items-start cookie-consent-drawer"
              data-testid="cookie-consent-drawer"
            >
              <div className="hidden sm:flex h-9 w-9 rounded-md bg-amber-400/15 border border-amber-400/40 items-center justify-center text-amber-300">
                <Info className="h-4 w-4"/>
              </div>
              <div className="text-[12.5px] text-zinc-200 leading-relaxed">
                <strong className="text-amber-300 font-extrabold uppercase tracking-wider text-[10px] block mb-1">What are cookies?</strong>
                Cookies are tiny data files your browser stores on your device. <strong>Buller Taxi</strong> uses them strictly for operational purposes: keeping you signed in across pages, remembering your active trip + booking state on our dispatch system, and recording your role (Rider / Driver / Dispatch) so we deliver the right map and meter to the right person. We do <strong>not</strong> use cookies for advertising profiling, third-party data brokering, or behavioural retargeting across other websites.
              </div>
              <button
                type="button"
                onClick={() => setExpanded(false)}
                aria-label="Close explainer"
                className="hidden sm:flex h-9 w-9 rounded-md text-zinc-400 hover:text-white hover:bg-white/10 items-center justify-center justify-self-end"
                data-testid="cookie-consent-drawer-close"
              >
                <X className="h-4 w-4"/>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
